import type { RunOutcome } from '../executionHistory';

export interface RunHeaderProps {
  /** The step this run executed. */
  step: string;
  /** Whether the run is still in progress (spec 097 FR-009). */
  live?: boolean;
  /** How the run ended, in the vocabulary every other surface uses. */
  outcome: RunOutcome;
}

/**
 * Which run this is, kept in view while its transcript scrolls (spec 096 FR-010).
 *
 * Rendered by the executions rail as a sibling *before* the run's collapsible
 * panel, not inside it. The panel is `overflow-hidden` for its height animation,
 * and an `overflow: hidden` ancestor is the box a `sticky` element sticks to —
 * inside the panel this header stuck to the panel and scrolled away with it.
 * Outside, its scrollport is `SheetBody`, the drawer's only scroll container,
 * and its containing block is the row, so it pins at the top of the drawer for
 * exactly as long as this run's row is in view. The row is a translated,
 * absolutely positioned list item; that constrains `fixed`, not `sticky`.
 *
 * The popover ground hides what scrolls underneath. Negative side margins let
 * the band reach the drawer's padding edge rather than stopping short of it.
 *
 * `-top-4`, not `top-0`. The drawer body scrolls with a 1rem top padding
 * (`TaskDrawer`, `p-4`), and a sticky `top: 0` pins at the padding box's inner
 * edge, so the header stopped a padding's height below the top and entries
 * scrolled through the strip above it in full view — the same band the board
 * canvas removes by carrying no top padding. Pinning a padding's height higher
 * puts the header on the edge; the negative offset changes nothing about where
 * it sits before it pins. Measured in Chromium against a scroller with 16px of
 * padding: `top: 0` pins 16px down, `top: -16px` pins on the edge.
 */
export function RunHeader({ step, outcome, live = false }: RunHeaderProps) {
  return (
    <div
      data-testid="run-header"
      className="bg-popover sticky -top-4 z-10 -mx-1 flex items-baseline gap-2 px-1 py-1 text-xs"
    >
      <span className="text-foreground font-medium">{step}</span>
      <span className={`${outcome.colourClass} font-medium`}>
        {outcome.label}
      </span>
      {live ? (
        // The word, not only the dot: the pulse is a scan aid and stops under
        // reduced motion; "Live" is what says it.
        <span className="text-primary inline-flex items-center gap-1 font-medium">
          <span
            aria-hidden="true"
            className="bg-primary size-1.5 rounded-full motion-safe:animate-pulse"
          />
          Live
        </span>
      ) : null}
    </div>
  );
}
